import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { portalApi, resolveFileUrl } from '../../../../lib/portalApi';
import KanbanBoard, { statusChip } from '../../shared/KanbanBoard';

const STATUS_LABEL = {
  pending: 'Pending',
  in_progress: 'In progress',
  on_hold: 'On hold',
  blocked: 'Blocked',
  needs_clarification: 'Need clarification',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const ONBOARDING_COLUMNS = [
  { id: 'pending', title: 'Pending' },
  { id: 'in_progress', title: 'In progress' },
  { id: 'waiting', title: 'Waiting' },
  { id: 'completed', title: 'Completed' },
];

function onboardingColumnId(status) {
  if (status === 'pending') return 'pending';
  if (status === 'in_progress') return 'in_progress';
  if (['on_hold', 'blocked', 'needs_clarification'].includes(status)) return 'waiting';
  if (['completed', 'cancelled'].includes(status)) return 'completed';
  return 'pending';
}

function chip(step) {
  const type =
    step.taskType === 'send_document'
      ? 'SEND DOCUMENT'
      : step.taskType === 'request_document'
        ? 'REQUEST DOCUMENT'
        : 'SCHEDULE MEETING';
  return `TASK · ${type} · ${(step.status || '').replace(/_/g, ' ').toUpperCase()}`;
}

export function AdminOnboardingQueuePage() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    portalApi.onboardingQueue().then(setRows).catch(() => setRows([]));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Onboarding</h1>
        <p className="text-white/50 text-sm">Accepted startups awaiting batch decision.</p>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-white/40">No startups in onboarding.</p>}
        {rows.map((s) => (
          <Link
            key={s.id}
            to={`/admin/onboarding/${s.id}`}
            className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-[#d42b92]/40"
          >
            <div>
              <div className="text-white font-medium">{s.name}</div>
              <div className="text-xs text-white/40">{s.founderEmail}</div>
            </div>
            <span className="text-xs text-[#ff8ec5]">{s.stage}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function AdminOnboardingBoardPage() {
  const { startupId } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [showDecide, setShowDecide] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [comment, setComment] = useState('');
  const [actionTitle, setActionTitle] = useState('');
  const [meeting, setMeeting] = useState({
    title: '',
    mode: 'online',
    scheduledAt: '',
    meetingLink: '',
    location: '',
  });
  const [momDrafts, setMomDrafts] = useState({});
  const [sendTitle, setSendTitle] = useState('');
  const [sendFile, setSendFile] = useState(null);
  const [reqForm, setReqForm] = useState({ title: '', instructions: '', requiresSignature: false });
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [newStep, setNewStep] = useState({ title: '', taskType: 'meeting', description: '' });

  const load = async () => {
    const data = await portalApi.onboardingBoard(startupId);
    setBoard(data);
    if (!activeId && data.steps?.[0]) setActiveId(data.steps[0].id);
    else if (activeId && data.steps && !data.steps.find((s) => s.id === activeId)) {
      setActiveId(data.steps[0]?.id || null);
    }
    return data;
  };

  useEffect(() => {
    load().catch((e) => setError(e.message));
    portalApi.assignableBatches().then(setBatches).catch(() => setBatches([]));
  }, [startupId]);

  const steps = board?.steps || [];
  const step = useMemo(() => steps.find((s) => s.id === activeId) || steps[0], [steps, activeId]);
  const allDone = board?.allDone || steps.every((s) => ['completed', 'cancelled'].includes(s.status));
  const locked = board?.startup?.stage && board.startup.stage !== 'accepted';

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const decide = async (decision) => {
    await run(async () => {
      await portalApi.decideOnboarding(startupId, {
        decision,
        batchId: decision === 'accept' ? batchId : undefined,
      });
      if (decision === 'accept') navigate(`/admin/batches/${batchId}/startups/${startupId}`);
      else if (decision === 'reject_delete') navigate('/admin/onboarding');
      else setShowDecide(false);
    });
  };

  if (!board && !error) return <p className="text-white/40">Loading…</p>;
  if (error && !board) return <p className="text-red-400">{error}</p>;

  const { startup, percent = 0, timeline = [] } = board;

  return (
    <div className="space-y-6">
      <Link to="/admin/onboarding" className="text-sm text-white/50 hover:text-[#ff8ec5]">
        ← Queue
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">{startup.name}</h1>
          <p className="text-white/50 text-sm">
            {startup.founderName} · {startup.founderEmail} · {startup.stage}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-1.5 w-40 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#d42b92]" style={{ width: `${percent}%` }} />
            </div>
            <span className="text-sm text-[#ff8ec5]">{percent}% onboarding</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              run(async () => {
                await portalApi.openDd(startupId, {});
              })
            }
            className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm"
          >
            Open Due Diligence
          </button>
          <button
            type="button"
            disabled={locked}
            onClick={() => setShowDecide((v) => !v)}
            className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
          >
            Finish onboarding
          </button>
        </div>
      </div>

      {showDecide && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div className="text-white font-medium">Decision</div>
          {!allDone && (
            <p className="text-amber-300/90 text-sm">
              Accept is disabled until every step is completed or cancelled.
            </p>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
            >
              <option value="">Select batch…</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <button
              disabled={busy || !batchId || !allDone || locked}
              onClick={() => decide('accept')}
              className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
            >
              Accept → batch
            </button>
            <button
              disabled={busy || locked}
              onClick={() => decide('reject_keep')}
              className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm"
            >
              Reject keep
            </button>
            <button
              disabled={busy || locked}
              onClick={() => decide('reject_delete')}
              className="px-3 py-2 rounded-xl bg-red-600/70 text-white text-sm"
            >
              Reject delete
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && !locked && (
              <button
                type="button"
                title="Add step here"
                onClick={() =>
                  run(async () => {
                    const created = await portalApi.addOnboardingStep(startupId, {
                      title: 'New step',
                      taskType: 'meeting',
                      afterStepId: steps[i - 1].id,
                    });
                    setActiveId(created.id);
                  })
                }
                className="shrink-0 w-6 h-6 rounded-full border border-dashed border-white/20 text-white/40 text-xs hover:border-[#d42b92] hover:text-[#ff8ec5]"
              >
                +
              </button>
            )}
            <button
              type="button"
              onClick={() => setActiveId(s.id)}
              className={`shrink-0 px-3 py-2 rounded-xl border text-sm ${
                step?.id === s.id
                  ? 'border-[#d42b92] bg-[#d42b92]/15 text-white'
                  : 'border-white/10 text-white/70 hover:border-white/25'
              }`}
            >
              <div className="font-medium">{s.title}</div>
              <div className="text-[10px] text-white/40">{STATUS_LABEL[s.status] || s.status}</div>
            </button>
          </React.Fragment>
        ))}
        {!locked && (
          <button
            type="button"
            onClick={() => setAddStepOpen(true)}
            className="shrink-0 px-3 py-2 rounded-xl border border-dashed border-white/20 text-white/60 text-sm hover:border-[#d42b92]"
          >
            Add Step
          </button>
        )}
      </div>

      {addStepOpen && (
        <div className="rounded-xl border border-white/10 p-4 space-y-2 max-w-md">
          <input
            value={newStep.title}
            onChange={(e) => setNewStep({ ...newStep, title: e.target.value })}
            placeholder="Step title"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          <select
            value={newStep.taskType}
            onChange={(e) => setNewStep({ ...newStep, taskType: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="meeting">Meeting</option>
            <option value="send_document">Send document</option>
            <option value="request_document">Request document</option>
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() =>
                run(async () => {
                  const created = await portalApi.addOnboardingStep(startupId, newStep);
                  setActiveId(created.id);
                  setAddStepOpen(false);
                  setNewStep({ title: '', taskType: 'meeting', description: '' });
                })
              }
              className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setAddStepOpen(false)}
              className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-[1fr_280px] gap-6">
        <div className="space-y-4">
          {step && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-white">{step.title} Actions</h3>
                  <p className="text-[11px] tracking-wide text-white/45 mt-1">{chip(step)}</p>
                  {step.description && (
                    <p className="text-sm text-white/50 mt-2">{step.description}</p>
                  )}
                  {step.deadline && (
                    <p className="text-xs text-[#ff8ec5] mt-1">
                      Deadline · {new Date(step.deadline).toLocaleString()}
                    </p>
                  )}
                </div>
                {!locked && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditTitle(step.title);
                        setEditDesc(step.description || '');
                        if (step.deadline) {
                          const d = new Date(step.deadline);
                          setEditDeadline(
                            new Date(d.getTime() - d.getTimezoneOffset() * 60000)
                              .toISOString()
                              .slice(0, 16)
                          );
                        } else {
                          setEditDeadline('');
                        }
                        setEditOpen(true);
                      }}
                      className="px-2.5 py-1.5 rounded-lg border border-white/15 text-xs text-white"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => run(() => portalApi.moveOnboardingStep(step.id, 'left'))}
                      className="px-2.5 py-1.5 rounded-lg border border-white/15 text-xs text-white"
                    >
                      Move Left
                    </button>
                    <button
                      type="button"
                      onClick={() => run(() => portalApi.moveOnboardingStep(step.id, 'right'))}
                      className="px-2.5 py-1.5 rounded-lg border border-white/15 text-xs text-white"
                    >
                      Move Right
                    </button>
                  </div>
                )}
              </div>

              {editOpen && (
                <div className="space-y-2 border-t border-white/10 pt-3">
                  <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <textarea
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    rows={2}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <label className="block text-xs text-white/40">
                    Deadline
                    <input
                      type="datetime-local"
                      value={editDeadline}
                      onChange={(e) => setEditDeadline(e.target.value)}
                      className="mt-1 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      run(async () => {
                        await portalApi.patchOnboardingStep(step.id, {
                          title: editTitle,
                          description: editDesc,
                          deadline: editDeadline
                            ? new Date(editDeadline).toISOString()
                            : null,
                        });
                        setEditOpen(false);
                      })
                    }
                    className="px-3 py-1.5 rounded-lg bg-[#d42b92] text-white text-xs"
                  >
                    Save
                  </button>
                </div>
              )}

              {/* Task-type panels */}
              {!locked && step.taskType === 'meeting' && (
                <div className="space-y-2 border-t border-white/10 pt-4">
                  <div className="text-sm text-white font-medium">Schedule Meeting</div>
                  <input
                    placeholder="Meeting title"
                    value={meeting.title}
                    onChange={(e) => setMeeting({ ...meeting, title: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={meeting.mode}
                      onChange={(e) => setMeeting({ ...meeting, mode: e.target.value })}
                      className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    >
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                    </select>
                    <input
                      type="datetime-local"
                      value={meeting.scheduledAt}
                      onChange={(e) => setMeeting({ ...meeting, scheduledAt: e.target.value })}
                      className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    />
                  </div>
                  {meeting.mode === 'online' ? (
                    <input
                      placeholder="Meeting link"
                      value={meeting.meetingLink}
                      onChange={(e) => setMeeting({ ...meeting, meetingLink: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    />
                  ) : (
                    <input
                      placeholder="Location"
                      value={meeting.location}
                      onChange={(e) => setMeeting({ ...meeting, location: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    />
                  )}
                  <button
                    type="button"
                    disabled={busy || !meeting.title}
                    onClick={() =>
                      run(async () => {
                        await portalApi.addMeeting(step.id, {
                          ...meeting,
                          scheduledAt: meeting.scheduledAt
                            ? new Date(meeting.scheduledAt).toISOString()
                            : null,
                        });
                        setMeeting({
                          title: '',
                          mode: 'online',
                          scheduledAt: '',
                          meetingLink: '',
                          location: '',
                        });
                      })
                    }
                    className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
                  >
                    {busy ? 'Working…' : 'Schedule Meeting'}
                  </button>
                </div>
              )}

              {!locked && step.taskType === 'send_document' && (
                <div className="space-y-2 border-t border-white/10 pt-4">
                  <div className="text-sm text-white font-medium">Upload & Send</div>
                  <input
                    placeholder="Title"
                    value={sendTitle}
                    onChange={(e) => setSendTitle(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="file"
                    onChange={(e) => setSendFile(e.target.files?.[0] || null)}
                    className="text-sm text-white/60"
                  />
                  <button
                    type="button"
                    disabled={busy || !sendFile}
                    onClick={() =>
                      run(async () => {
                        const fd = new FormData();
                        fd.append('file', sendFile);
                        fd.append('title', sendTitle || sendFile.name);
                        await portalApi.sendOnboardingDoc(step.id, fd);
                        setSendTitle('');
                        setSendFile(null);
                      })
                    }
                    className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
                  >
                    {busy ? 'Working…' : 'Upload & Send'}
                  </button>
                </div>
              )}

              {!locked && step.taskType === 'request_document' && (
                <div className="space-y-2 border-t border-white/10 pt-4">
                  <div className="text-sm text-white font-medium">Create Request</div>
                  <input
                    placeholder="Title"
                    value={reqForm.title}
                    onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <textarea
                    placeholder="Instructions"
                    value={reqForm.instructions}
                    onChange={(e) => setReqForm({ ...reqForm, instructions: e.target.value })}
                    rows={2}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                  />
                  <label className="flex items-center gap-2 text-sm text-white/70">
                    <input
                      type="checkbox"
                      checked={reqForm.requiresSignature}
                      onChange={(e) =>
                        setReqForm({ ...reqForm, requiresSignature: e.target.checked })
                      }
                    />
                    Requires signature
                  </label>
                  <button
                    type="button"
                    disabled={busy || !reqForm.title}
                    onClick={() =>
                      run(async () => {
                        await portalApi.addDocRequest(step.id, reqForm);
                        setReqForm({ title: '', instructions: '', requiresSignature: false });
                      })
                    }
                    className="px-3 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
                  >
                    {busy ? 'Working…' : 'Create Request'}
                  </button>
                </div>
              )}

              {/* Existing artifacts */}
              {step.meetings?.length > 0 && (
                <div className="space-y-3 text-sm text-white/65">
                  {step.meetings.map((m) => {
                    const momValue =
                      momDrafts[m.id] !== undefined ? momDrafts[m.id] : m.mom || '';
                    return (
                      <div
                        key={m.id}
                        className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-2"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-white font-medium">{m.title}</div>
                            <div className="text-xs text-white/40">
                              {m.scheduledAt
                                ? new Date(m.scheduledAt).toLocaleString()
                                : 'Unscheduled'}
                              {m.meetingLink ? (
                                <>
                                  {' '}
                                  ·{' '}
                                  <a
                                    href={m.meetingLink}
                                    className="text-[#ff8ec5]"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    link
                                  </a>
                                </>
                              ) : null}
                            </div>
                          </div>
                          {!locked && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                run(async () => {
                                  if (!window.confirm('Delete this meeting?')) return;
                                  await portalApi.deleteMeeting(m.id);
                                })
                              }
                              className="text-xs text-red-400/90 disabled:opacity-40"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-white/50 uppercase tracking-wide">
                          Minutes of Meeting
                        </div>
                        <textarea
                          rows={3}
                          placeholder="Add MOM after the meeting…"
                          value={momValue}
                          disabled={locked || busy}
                          onChange={(e) =>
                            setMomDrafts((prev) => ({ ...prev, [m.id]: e.target.value }))
                          }
                          className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm disabled:opacity-50"
                        />
                        {!locked && momValue !== (m.mom || '') && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                await portalApi.updateMeeting(m.id, { mom: momValue });
                                setMomDrafts((prev) => {
                                  const next = { ...prev };
                                  delete next[m.id];
                                  return next;
                                });
                              })
                            }
                            className="px-3 py-1.5 rounded-lg bg-[#d42b92] text-white text-xs disabled:opacity-40"
                          >
                            Save MOM
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {step.sentDocuments?.length > 0 && (
                <div className="space-y-1 text-sm text-white/65">
                  {step.sentDocuments.map((d) => (
                    <div key={d.id}>
                      Sent: {d.title} · {d.seenAt ? 'Seen' : 'Not seen'}
                      {d.url ? (
                        <>
                          {' '}
                          ·{' '}
                          <a
                            href={resolveFileUrl(d.url)}
                            className="text-[#ff8ec5]"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open file
                          </a>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
              {step.documentRequests?.length > 0 && (
                <div className="space-y-2">
                  {step.documentRequests.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm text-white/70">
                      <span>
                        Request: {d.title} · {d.status}
                        {d.submittedOriginalName ? ` · ${d.submittedOriginalName}` : ''}
                      </span>
                      {d.submittedUrl && (
                        <a
                          href={resolveFileUrl(d.submittedUrl)}
                          className="text-xs text-[#ff8ec5]"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open file
                        </a>
                      )}
                      {d.status === 'submitted' && !locked && (
                        <>
                          <button
                            type="button"
                            onClick={() => run(() => portalApi.reviewDocRequest(d.id, 'approved'))}
                            className="text-xs text-emerald-400"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => run(() => portalApi.reviewDocRequest(d.id, 'rejected'))}
                            className="text-xs text-red-400"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Action items */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="text-sm text-white font-medium">Action Items</div>
                {(step.actionItems || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-sm text-white/70">
                    <input
                      type="checkbox"
                      checked={!!a.done}
                      disabled={locked}
                      onChange={() => run(() => portalApi.toggleActionItem(a.id))}
                    />
                    <span className={a.done ? 'line-through opacity-50' : ''}>{a.title}</span>
                    {!locked && (
                      <button
                        type="button"
                        onClick={() => run(() => portalApi.deleteActionItem(a.id))}
                        className="text-xs text-white/30 hover:text-red-400 ml-auto"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
                {!locked && (
                  <div className="flex gap-2">
                    <input
                      value={actionTitle}
                      onChange={(e) => setActionTitle(e.target.value)}
                      placeholder="New action"
                      className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        run(async () => {
                          if (!actionTitle.trim()) return;
                          await portalApi.addActionItem(step.id, { title: actionTitle.trim() });
                          setActionTitle('');
                        })
                      }
                      className="px-3 py-1.5 rounded-lg border border-white/15 text-xs text-white"
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>

              {/* Comments */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="text-sm text-white font-medium">Comments</div>
                {(step.comments || []).map((c) => (
                  <div key={c.id} className="text-sm text-white/60">
                    <span className="text-white/40">{c.author || 'Staff'}: </span>
                    {c.body}
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add comment"
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      run(async () => {
                        if (!comment.trim()) return;
                        await portalApi.addComment(step.id, { body: comment.trim() });
                        setComment('');
                      })
                    }
                    className="px-3 py-1.5 rounded-lg border border-white/15 text-xs text-white"
                  >
                    Post
                  </button>
                </div>
              </div>

              {/* Manager controls */}
              {!locked && (
                <div className="border-t border-white/10 pt-4 flex flex-wrap gap-2">
                  {[
                    ['on_hold', 'On Hold'],
                    ['blocked', 'Blocked'],
                    ['needs_clarification', 'Need Clarification'],
                    ['cancelled', 'Cancel'],
                  ].map(([st, label]) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => run(() => portalApi.setStepStatus(step.id, st))}
                      className="px-2.5 py-1.5 rounded-lg border border-white/15 text-xs text-white/80"
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => run(() => portalApi.completeStep(step.id))}
                    className="px-2.5 py-1.5 rounded-lg bg-[#d42b92]/80 text-xs text-white"
                  >
                    Force Complete
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      run(async () => {
                        if (!window.confirm('Delete this step?')) return;
                        await portalApi.deleteOnboardingStep(step.id);
                      })
                    }
                    className="px-2.5 py-1.5 rounded-lg bg-red-600/60 text-xs text-white ml-auto"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Kanban */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-white font-medium">Board</h3>
                <p className="text-xs text-white/40">Drag steps to update status</p>
              </div>
            </div>
            <KanbanBoard
              columns={ONBOARDING_COLUMNS}
              disabled={busy}
              className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
              items={steps.map((s) => ({
                ...s,
                columnId: onboardingColumnId(s.status),
              }))}
              renderCard={(s) => (
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => setActiveId(s.id)}
                >
                  <div className="text-sm text-white font-medium">{s.title}</div>
                  <div className="mt-1 flex items-center gap-2">
                    {statusChip(s.status)}
                    <span className="text-[10px] text-white/35 uppercase">
                      {(s.taskType || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                </button>
              )}
              onMove={(itemId, _from, to) =>
                run(async () => {
                  const s = steps.find((x) => x.id === itemId);
                  if (!s) return;
                  const cur = onboardingColumnId(s.status);
                  if (cur === to) return;
                  if (to === 'pending') {
                    await portalApi.setStepStatus(itemId, 'pending');
                    return;
                  }
                  if (to === 'in_progress') {
                    await portalApi.setStepStatus(itemId, 'in_progress');
                    return;
                  }
                  if (to === 'waiting') {
                    await portalApi.setStepStatus(itemId, 'on_hold');
                    return;
                  }
                  if (to === 'completed') {
                    await portalApi.completeStep(itemId);
                  }
                })
              }
            />
          </div>
        </div>

        {/* Timeline */}
        <aside className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 h-fit">
          <h3 className="text-white font-medium mb-3">Timeline</h3>
          {(!timeline || timeline.length === 0) && (
            <p className="text-sm text-white/40">No completed or cancelled steps yet</p>
          )}
          <ul className="space-y-3">
            {(timeline || []).map((s) => (
              <li key={s.id} className="text-sm">
                <div className="text-white">{s.title}</div>
                <div className="text-xs text-white/40">
                  {STATUS_LABEL[s.status] || s.status}
                  {s.completedAt ? ` · ${new Date(s.completedAt).toLocaleDateString()}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
